// Spaghetti land levels. The id is fixed (progress is saved under it) – don't change it once the level is published.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { StaticData } from '../../core/types';

const L12_STAT: StaticData[] = [{ x: 522, y: 585, w: 10, h: 40, skin: 'spaghetti_cheese' }, { x: 400, y: 700, w: 60, h: 200, skin: 'spaghetti_cheese' }];
const P = 15;                                  // meatball radius
const pasta = (x: number, y: number, w: number, a?: number, h?: number): StaticData => ({ x, y, w, h: h || 22, a: a || 0, skin: 'spaghetti_pasta' });
const clothBlock = (x: number, y: number, w: number, h?: number): StaticData => ({ x, y, w, h: h || 30, skin: 'spaghetti_tablecloth' });
const cheeseBlock = (x: number, y: number, w: number, h: number): StaticData => ({ x, y, w, h, skin: 'spaghetti_cheese' });

export const levels = defineLevels([
  // 1 – The tomato pushes – hang the pendulum on the hook and pull it back (new: pendulum)
  (() => {
    const s1 = pasta(250, 420, 300);
    return {
      id: 'tomato-push', par: 1, tools: { spaghetti_pendulum: 1 }, learn: ['pendulum', 'energy'], intro: 'spaghetti_pendulum', time: 12,
      statics: [s1, pasta(540, 500, 320, 24), clothBlock(830, 664, 300)],
      balls: [on(s1, 70, P)],
      goal: { type: 'bowl', x: 830, y: 640, w: 180 },
      objs: [{ t: 'spaghetti_hook', x: 270, y: 225 }, { t: 'duck', x: 660, y: 470 }],
      solution: [{ t: 'spaghetti_pendulum', x: 270, y: 225, a: 60 }],
    };
  })(),

  // 2 – Snip! – cut the string: the meatball hangs above the plate (new: scissors)
  {
    id: 'snip', par: 1, tools: { spaghetti_scissors: 1 }, learn: ['string', 'gravity'], intro: 'spaghetti_scissors', time: 12,
    statics: [clothBlock(640, 664, 340)],
    balls: [{ x: 570, y: 320, r: P }],
    goal: { type: 'bowl', x: 640, y: 640, w: 180 },
    objs: [{ t: 'spaghetti_string', ax: 640, ay: 90 }, { t: 'duck', x: 900, y: 300 }],
    solution: [{ t: 'spaghetti_scissors', x: 640, y: 210 }],
  },

  // 3 – Rope bridge – a bridge across the gap (new: bridge)
  (() => {
    const s1 = pasta(200, 360, 300, 8), s2 = pasta(800, 482, 300, 4);
    return {
      id: 'rope-bridge', par: 1, tools: { spaghetti_bridge: 1 }, learn: ['bridge', 'ramp'], intro: 'spaghetti_bridge',
      statics: [s1, s2, clothBlock(1040, 664, 240)],
      balls: [on(s1, -120, P)],
      goal: { type: 'bowl', x: 1040, y: 640, w: 180 },
      objs: [{ t: 'duck', x: 500, y: 330 }],
      solution: [{ t: 'spaghetti_bridge', x: 500, y: 430, a: 15 }],
    };
  })(),

  // 4 – Long string – long or short? The hook is high up: only the long string reaches the meatball (new: long pendulum; decoy: short pendulum)
  (() => {
    const s1 = pasta(300, 481, 340);
    return {
      id: 'long-string', par: 1, tools: { spaghetti_pendulum: 1, spaghetti_long_pendulum: 1 }, learn: ['pendulum', 'string'], intro: 'spaghetti_long_pendulum',
      statics: [s1, pasta(640, 540, 360, 20), clothBlock(910, 724, 260)],
      balls: [on(s1, 50, P)],
      goal: { type: 'bowl', x: 910, y: 700, w: 180 },
      objs: [{ t: 'spaghetti_hook', x: 300, y: 170 }, { t: 'duck', x: 160, y: 330 }],
      solution: [{ t: 'spaghetti_long_pendulum', x: 300, y: 170, a: 60 }],
      fails: [[{ t: 'spaghetti_pendulum', x: 300, y: 170, a: 60 }], [{ t: 'spaghetti_pendulum', x: 300, y: 170, a: 90 }]],
    };
  })(),

  // 5 – High swing – pull it high: a high swing = a hard push, so the meatball can jump the gap
  (() => {
    const s1 = pasta(260, 420, 300), s2 = pasta(700, 481, 340, 3);
    return {
      id: 'high-swing', par: 1, tools: { spaghetti_pendulum: 1 }, learn: ['pendulum', 'energy'],
      statics: [s1, s2, clothBlock(1040, 664, 240)],
      balls: [on(s1, 90, P)],
      goal: { type: 'bowl', x: 1040, y: 640, w: 180 },
      objs: [{ t: 'spaghetti_hook', x: 300, y: 225 }, { t: 'duck', x: 520, y: 300 }],
      solution: [{ t: 'spaghetti_pendulum', x: 300, y: 225, a: 90 }],
      fails: [[{ t: 'spaghetti_pendulum', x: 300, y: 225, a: 30 }]],
    };
  })(),

  // 6 – Let go mid-swing – the meatball swings, the scissors decide where it lets go
  {
    id: 'let-go-mid-swing', par: 1, tools: { spaghetti_scissors: 1 }, learn: ['string', 'pendulum'],
    statics: [clothBlock(1000, 600, 260), cheeseBlock(780, 640, 40, 260)],
    balls: [{ x: 340, y: 250, r: P }],
    goal: { type: 'bowl', x: 1000, y: 576, w: 180 },
    objs: [{ t: 'spaghetti_string', ax: 600, ay: 100 }, { t: 'duck', x: 600, y: 470 }],
    solution: [{ t: 'spaghetti_scissors', x: 700, y: 330 }],
    fails: [[{ t: 'spaghetti_scissors', x: 420, y: 290 }]],
  },

  // 7 – Too far – bridge or lasagne sheet? The gap is too wide for the sheet (decoy: plank)
  (() => {
    const s1 = pasta(170, 330, 260, 10), s2 = pasta(800, 470, 330, 3);
    return {
      id: 'too-far', par: 1, tools: { spaghetti_bridge: 1, plank: 1 }, learn: ['bridge', 'materials'],
      statics: [s1, s2, clothBlock(1100, 664, 160)],
      balls: [on(s1, -100, P)],
      goal: { type: 'bowl', x: 1100, y: 640, w: 150 },
      objs: [{ t: 'duck', x: 900, y: 360 }],
      solution: [{ t: 'spaghetti_bridge', x: 450, y: 400, a: 15 }],
      fails: [[{ t: 'plank', x: 380, y: 380, a: 15 }], [{ t: 'plank', x: 600, y: 440, a: 15 }]],
    };
  })(),

  // 8 – Which way? – the plate is on the left: the tomato must swing from the right (decoy: long pendulum)
  (() => {
    const s1 = pasta(600, 420, 300);
    return {
      id: 'which-way', par: 1, tools: { spaghetti_pendulum: 1, spaghetti_long_pendulum: 1 }, learn: ['pendulum', 'direction'],
      statics: [s1, clothBlock(240, 724, 300), cheeseBlock(88, 600, 40, 260)],
      balls: [on(s1, 40, P)],
      goal: { type: 'bowl', x: 240, y: 700, w: 240 },
      objs: [{ t: 'spaghetti_hook', x: 600, y: 225 }, { t: 'duck', x: 860, y: 330 }],
      solution: [{ t: 'spaghetti_pendulum', x: 600, y: 225, a: -60 }],
      fails: [[{ t: 'spaghetti_pendulum', x: 600, y: 225, a: 60 }], [{ t: 'spaghetti_long_pendulum', x: 600, y: 225, a: -60 }]],
    };
  })(),

  // 9 – Up and over – cut on the way up: the meatball must fly over the cheese to the high plate
  {
    id: 'up-and-over', par: 1, tools: { spaghetti_scissors: 1 }, learn: ['string', 'pendulum', 'energy'],
    statics: [clothBlock(940, 494, 240), cheeseBlock(850, 650, 60, 330)],
    balls: [{ x: 237, y: 216, r: P }],
    goal: { type: 'bowl', x: 940, y: 470, w: 180 },
    objs: [{ t: 'spaghetti_string', ax: 500, ay: 120 }, { t: 'duck', x: 500, y: 470 }],
    solution: [{ t: 'spaghetti_scissors', x: 680, y: 334 }],
    fails: [[{ t: 'spaghetti_scissors', x: 500, y: 330 }], [{ t: 'spaghetti_scissors', x: 330, y: 300 }]],
  },

  // 10 – Pass it on – the tomato passes the motion on: it pushes the hanging meatball, the scissors let it go (two steps; decoy: short pendulum)
  {
    id: 'pass-it-on', par: 2, tools: { spaghetti_pendulum: 1, spaghetti_long_pendulum: 1, spaghetti_scissors: 1 }, learn: ['pendulum', 'string', 'energy'],
    statics: [clothBlock(980, 640, 240)],
    balls: [{ x: 600, y: 360, r: P }],
    goal: { type: 'bowl', x: 980, y: 616, w: 180 },
    objs: [{ t: 'spaghetti_string', ax: 600, ay: 110 }, { t: 'spaghetti_hook', x: 400, y: 200 }, { t: 'duck', x: 300, y: 560 }],
    solution: [{ t: 'spaghetti_long_pendulum', x: 400, y: 200, a: 90 }, { t: 'spaghetti_scissors', x: 760, y: 300 }],
    fails: [[{ t: 'spaghetti_scissors', x: 760, y: 300 }], [{ t: 'spaghetti_long_pendulum', x: 400, y: 200, a: 90 }]],
  },

  // 11 – Push over the bridge – push and bridge: long pendulum + bridge (decoy: short pendulum)
  (() => {
    const s1 = pasta(220, 440, 300), s2 = pasta(820, 556, 320, 4);
    return {
      id: 'push-over-the-bridge', par: 2, tools: { spaghetti_long_pendulum: 1, spaghetti_pendulum: 1, spaghetti_bridge: 1 }, learn: ['pendulum', 'bridge'],
      statics: [s1, s2, clothBlock(1080, 724, 180)],
      balls: [on(s1, 40, P)],
      goal: { type: 'bowl', x: 1080, y: 700, w: 160 },
      objs: [{ t: 'spaghetti_hook', x: 220, y: 135 }, { t: 'duck', x: 540, y: 380 }],
      solution: [{ t: 'spaghetti_long_pendulum', x: 220, y: 135, a: 60 }, { t: 'spaghetti_bridge', x: 520, y: 470, a: 15 }],
      fails: [[{ t: 'spaghetti_long_pendulum', x: 220, y: 135, a: 60 }], [{ t: 'spaghetti_bridge', x: 520, y: 470, a: 15 }]],
    };
  })(),

  // 12 – Cheese catapult – cut the string, the parmesan falls onto the spoon, the meatball flies up (decoys: pendulum, second plank)
  {
    id: 'cheese-catapult', par: 2, tools: { spaghetti_scissors: 1, plank: 2, spaghetti_pendulum: 1 }, learn: ['string', 'lever'],
    statics: L12_STAT,
    fixed: [{ t: 'seesaw', x: 400, y: 560, a: 20 }],
    balls: [{ x: 495, y: 570, r: P }],
    goal: { type: 'bell', x: 500, y: 330 },
    objs: [{ t: 'spaghetti_string', ax: 170, ay: 90, cheese: true, x: 170, y: 270 }, { t: 'duck', x: 760, y: 420 }],
    solution: [{ t: 'spaghetti_scissors', x: 170, y: 190 }, { t: 'plank', x: 165, y: 370, a: 15 }],
    fails: [[{ t: 'spaghetti_scissors', x: 170, y: 190 }], [{ t: 'plank', x: 165, y: 370, a: 15 }]],
  },

  // 13 – Let go and bridge – swing, let go and bridge: the meatball is let go onto the shelf and rolls over the bridge (decoy: pad)
  (() => {
    const s1 = pasta(640, 430, 220);
    return {
      id: 'let-go-and-bridge', par: 2, tools: { spaghetti_scissors: 1, spaghetti_bridge: 1, pad: 1 }, learn: ['string', 'bridge', 'pendulum'],
      statics: [s1, clothBlock(1070, 524, 200), pasta(880, 250, 340)],
      balls: [{ x: 90, y: 240, r: P }],
      goal: { type: 'bowl', x: 1070, y: 500, w: 170 },
      objs: [{ t: 'spaghetti_string', ax: 350, ay: 90 }, { t: 'duck', x: 900, y: 330 }],
      solution: [{ t: 'spaghetti_scissors', x: 450, y: 330 }, { t: 'spaghetti_bridge', x: 900, y: 425, a: 0 }],
      fails: [[{ t: 'spaghetti_scissors', x: 450, y: 330 }], [{ t: 'spaghetti_bridge', x: 900, y: 425, a: 0 }]],
    };
  })(),

  // 14 – Push, bridge and hop – combine: tomato push, bridge and ravioli hop (three steps; decoys: short pendulum, plank)
  (() => {
    const s1 = pasta(200, 400, 260), s2 = pasta(740, 481, 200);
    return {
      id: 'push-bridge-hop', par: 3, tools: { spaghetti_long_pendulum: 1, spaghetti_pendulum: 1, spaghetti_bridge: 1, pad: 1, plank: 1 }, learn: ['pendulum', 'bridge', 'spring'],
      statics: [s1, s2, clothBlock(900, 744, 300, 24), cheeseBlock(1000, 640, 50, 220), clothBlock(1095, 584, 150)],
      balls: [on(s1, 30, P)],
      goal: { type: 'bowl', x: 1095, y: 560, w: 140 },
      objs: [{ t: 'spaghetti_hook', x: 190, y: 95 }, { t: 'duck', x: 480, y: 330 }],
      solution: [{ t: 'spaghetti_long_pendulum', x: 190, y: 95, a: 60 }, { t: 'spaghetti_bridge', x: 480, y: 440, a: 15 }, { t: 'pad', x: 900, y: 640, a: 15 }],
      fails: [[{ t: 'spaghetti_long_pendulum', x: 190, y: 95, a: 60 }, { t: 'spaghetti_bridge', x: 480, y: 440, a: 15 }]],
    };
  })(),

  // 15 – The master chef – two meatballs: one must be pushed, one cut free – and a bridge to the plate (decoys: long pendulum, planks, pad)
  (() => {
    const s1 = pasta(170, 260, 240), s2 = pasta(480, 470, 360, 10);
    return {
      id: 'master-chef', par: 3, tools: { spaghetti_pendulum: 1, spaghetti_long_pendulum: 1, spaghetti_scissors: 1, spaghetti_bridge: 1, plank: 2, pad: 1 }, learn: ['pendulum', 'string', 'bridge', 'counting'],
      statics: [s1, s2, clothBlock(1050, 724, 240)],
      balls: [on(s1, 50, P), { x: 520, y: 330, r: P }],
      goal: { type: 'bowl', x: 1050, y: 700, w: 200, count: 2 },
      objs: [{ t: 'spaghetti_hook', x: 170, y: 72 }, { t: 'spaghetti_string', ax: 520, ay: 90, ball: 1 }, { t: 'duck', x: 820, y: 330 }],
      solution: [{ t: 'spaghetti_pendulum', x: 170, y: 72, a: 60 }, { t: 'spaghetti_scissors', x: 520, y: 220 }, { t: 'spaghetti_bridge', x: 800, y: 540, a: 15 }],
      fails: [[{ t: 'spaghetti_scissors', x: 520, y: 220 }, { t: 'spaghetti_bridge', x: 800, y: 540, a: 15 }], [{ t: 'spaghetti_pendulum', x: 170, y: 72, a: 60 }, { t: 'spaghetti_bridge', x: 800, y: 540, a: 15 }], [{ t: 'spaghetti_pendulum', x: 170, y: 72, a: 60 }, { t: 'spaghetti_scissors', x: 520, y: 220 }]],
    };
  })(),
]);
